'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import type { OutputRenderContext } from '../../tool-config-types';
import { useFindingSearchContext } from '../../../finding-search/ChatFindingSearchProvider';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { ViewExecutionAction } from './ViewExecutionAction';

type AttackerExecutionTrackingRowProps = {
  executionId: string;
  signalId?: string;
  signalTitle?: string;
  isRunning: boolean;
  onOpenObject?: OutputRenderContext['onOpenObject'];
  headerActions?: ReactNode;
};

function useResolvedSignalTitle({
  signalId,
  signalTitle,
}: {
  signalId?: string;
  signalTitle?: string;
}): string | undefined {
  const { onGetSignalById } = useFindingSearchContext();
  const providedTitle = signalTitle?.trim() || undefined;
  const [resolvedTitle, setResolvedTitle] = useState<string | undefined>(
    providedTitle,
  );

  useEffect(() => {
    if (providedTitle) {
      setResolvedTitle(providedTitle);

      return;
    }

    if (!signalId || !onGetSignalById) {
      return;
    }

    let cancelled = false;

    onGetSignalById(signalId)
      .then((signal) => {
        if (!cancelled) {
          setResolvedTitle(signal?.label?.trim() || undefined);
        }
      })
      .catch((error: unknown) => {
        console.warn(
          'Failed to resolve attack execution signal title; showing generic label',
          error,
        );
      });

    return () => {
      cancelled = true;
    };
  }, [providedTitle, signalId, onGetSignalById]);

  return resolvedTitle;
}

export function AttackerExecutionTrackingRow({
  executionId,
  signalId,
  signalTitle,
  isRunning,
  onOpenObject,
  headerActions,
}: AttackerExecutionTrackingRowProps) {
  const t = useTranslations('designSystem.chat.attackerTool');
  const resolvedTitle = useResolvedSignalTitle({ signalId, signalTitle });
  const title = resolvedTitle
    ? t('attackExecutionOn', { signal: resolvedTitle })
    : t('attackExecution');

  return (
    <InlineExecutionToolCard
      title={title}
      status={isRunning ? 'in-progress' : undefined}
      headerActions={
        <>
          {headerActions}
          <ViewExecutionAction
            executionId={executionId}
            onOpenObject={onOpenObject}
          />
        </>
      }
    />
  );
}
